// 页：⑥ 实验录入（占位）
// 状态：待跟前端 + 业务 + 工程一起对齐方案后实现
// 见 ../docs/experiment-editor-kickoff.md 筹备文档

import React from 'react';
import { Card, Alert, Steps, Tag, Row, Col, Divider } from 'antd';
import { FileTextOutlined, TeamOutlined, ApiOutlined, BulbOutlined } from '@ant-design/icons';

const ExperimentEditor: React.FC = () => {
  return (
    <div style={{ padding: '16px 24px' }}>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row align="middle" gutter={12}>
          <Col flex="120px"><strong>🛠️ 实验录入</strong></Col>
          <Col flex="auto">
            <Tag color="warning">占位 · 待三方对齐</Tag>
            <span style={{ fontSize: 11, color: '#8c8c8c', marginLeft: 8 }}>
              业务方/前端工程/产品三方对齐 spec 后实施
            </span>
          </Col>
        </Row>
      </Card>

      <Alert
        type="info"
        showIcon
        icon={<BulbOutlined />}
        message="为什么这页要占位"
        description={
          <span style={{ fontSize: 12 }}>
            实验录入页涉及 ① 业务方实际工作流 ② Shopify Liquid theme A/B 接入方式 ③ DOM 变化谁来写。
            这三件事都需要业务同学、前端工程师、产品三方同时在场对齐，凭单方脑补的方案大概率落不到地。
            筹备文档已落到 <code>src/IdpAiDemo/docs/experiment-editor-kickoff.md</code>。
          </span>
        }
        style={{ marginBottom: 16 }}
      />

      <Card title={<span><TeamOutlined /> 三方对齐流程</span>} size="small" style={{ marginBottom: 16 }}>
        <Steps current={0} size="small">
          <Steps.Step
            title="筹备会议"
            description={<span style={{ fontSize: 11 }}>柴进开会前发筹备文档给前端 + 业务</span>}
            status="process"
          />
          <Steps.Step
            title="对齐方案"
            description={<span style={{ fontSize: 11 }}>三方案 A/B/C 拍板</span>}
            status="wait"
          />
          <Steps.Step
            title="确定模板池（若选 B）"
            description={<span style={{ fontSize: 11 }}>戒指品类下 6 个起步模板</span>}
            status="wait"
          />
          <Steps.Step
            title="工程接入"
            description={<span style={{ fontSize: 11 }}>Shopify theme SDK + DOM 切换代码</span>}
            status="wait"
          />
          <Steps.Step
            title="录入页实施"
            description={<span style={{ fontSize: 11 }}>业务方自助实验配置</span>}
            status="wait"
          />
        </Steps>
      </Card>

      <Card title={<span><ApiOutlined /> 三种实施方案对比（会议靶子）</span>} size="small" style={{ marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
              <th style={{ padding: 8, textAlign: 'left', width: 80 }}>方案</th>
              <th style={{ padding: 8, textAlign: 'left' }}>业务方自助度</th>
              <th style={{ padding: 8, textAlign: 'left' }}>工程接入量</th>
              <th style={{ padding: 8, textAlign: 'left' }}>灵活度</th>
              <th style={{ padding: 8, textAlign: 'left' }}>风险</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px dashed #f5f5f5' }}>
              <td style={{ padding: 8 }}><Tag>A</Tag><br /><small>每实验工程接入</small></td>
              <td style={{ padding: 8 }}>录入 key/MDE，其余等工程</td>
              <td style={{ padding: 8 }}>每个新实验都要工程改 theme</td>
              <td style={{ padding: 8 }}>最高（任意改）</td>
              <td style={{ padding: 8 }}>实验迭代速度受工程瓶颈</td>
            </tr>
            <tr style={{ borderBottom: '1px dashed #f5f5f5', background: '#f6ffed' }}>
              <td style={{ padding: 8 }}><Tag color="green">B</Tag><br /><small>模板化（推荐）</small></td>
              <td style={{ padding: 8 }}>选模板 + 填参数 完全自助</td>
              <td style={{ padding: 8 }}>一次性接 6 个模板（约 2 周）</td>
              <td style={{ padding: 8 }}>中（限模板内）</td>
              <td style={{ padding: 8 }}>模板池设计要覆盖 80% 场景</td>
            </tr>
            <tr>
              <td style={{ padding: 8 }}><Tag>C</Tag><br /><small>自由 JS/CSS 注入</small></td>
              <td style={{ padding: 8 }}>业务方贴代码片段</td>
              <td style={{ padding: 8 }}>0</td>
              <td style={{ padding: 8 }}>高（任意改）</td>
              <td style={{ padding: 8 }}><span style={{ color: '#cf1322' }}>极高 — 写错代码全站崩</span></td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Row gutter={12}>
        <Col span={12}>
          <Card title={<span><FileTextOutlined /> 待业务同学填的清单</span>} size="small">
            <ul style={{ fontSize: 12, paddingLeft: 16, color: '#595959', lineHeight: 1.8 }}>
              <li>过去半年想做但没做的实验有哪些？典型频次？</li>
              <li>实验决策习惯：等多久看结果？什么样的 lift 算"显著"？</li>
              <li>戒指品类下，最常想测的元素 Top 5（价格格式 / 主图风格 / 描述长短 / CTA 文案 / 促销 banner / 尺码引导...）</li>
              <li>实验"失败"的处理流程：放弃 / 改参数继续 / 换品类再试？</li>
              <li>是否需要"实验上线前 dry-run 预览"功能？</li>
            </ul>
          </Card>
        </Col>
        <Col span={12}>
          <Card title={<span><FileTextOutlined /> 待前端同学回的</span>} size="small">
            <ul style={{ fontSize: 12, paddingLeft: 16, color: '#595959', lineHeight: 1.8 }}>
              <li>FG/ST/BF 三个独立站当前 theme 有没有现成 A/B 工具？</li>
              <li>接 SDK 的 effort 预估（每个 theme 工时 + 全量回归测试）</li>
              <li>SDK 维护谁负责？跨 3 个 theme 改动同步成本</li>
              <li>checkout 页改不了的实验（spec §4.9 已明），有没有变通方案？</li>
              <li>postMessage 协议是否影响 PageSpeed / LCP 指标？</li>
            </ul>
          </Card>
        </Col>
      </Row>

      <Divider plain style={{ marginTop: 24, fontSize: 11, color: '#8c8c8c' }}>
        当前 demo 边界：上面三个 panel 是会议筹备文档的可视化镜像；真实实验录入页待方案对齐后实施
      </Divider>
    </div>
  );
};

export default ExperimentEditor;
